'use client';

import React, { useCallback, useMemo } from 'react';
import type { CartGiftListProps, GiftItemForDisplay, CartItemReferenceLogic } from './cart-gift-list.types';
import { Icon, TextIcon, PromotionalItemCard, cn } from '@kidslabs/mario-nextjs-react-ui-core';
import styles from './cart-gift-list.module.css';
// TODO: Replace with @kidslabs/mario-nextjs-react-ui-core when PromotionalItemCard is available
import { ActionType, cartStoreAPI } from '@kidslabs/module-quote';
import { observer, useSelector } from '@legendapp/state/react';

/**
 * CartGiftList Component
 *
 * Consumer component that handles list rendering and business data transformation.
 * Transforms CartItemReferenceLogic (with gift actions) to presentation props for PromotionalItemCard.
 *
 * ARCHITECTURE:
 * - This component belongs to the consumer module (module-flash-sale)
 * - It transforms business data (CartItemReferenceLogic) → presentation props
 * - Uses PromotionalItemCard from mario-nextjs-react-ui-core for rendering
 * - Filters actions to only show GIFT type actions
 * - Uses centralized GiftChangeParams type for callback parameters
 *
 * @example
 * ```tsx
 * <CartGiftList
 *   cartItemReference={cartItemReference}
 *   mode="compact"
 *   onActionPress={({cartItemReference, logic}) => {
 *     console.log('Full context:', cartItemReference);
 *     console.log('Pressed logic:', logic);
 *     handleChangeGift({cartItemReference, logic});
 *   }}
 *   defaultLogicLabel="Free Gift Promotion"
 * />
 * ```
 */
export const CartGiftList: React.FC<CartGiftListProps> = observer(
    ({
        cartItemReference,
        mode = 'compact',
        size,
        innerContainerClassName,
        onActionPress,
        defaultLogicLabel,
        showActionButton,
    }) => {
        // GET STOCK DATA FROM Legend-State store
        const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());

        /**
         * PARSE ERROR MESSAGE - Extract user-facing message from server error format
         * Server format: "items[X].logics[Y]: <message>" or "items[X].logics[Y].actions[Z]: <message>"
         * Returns the part after ": " or the full message if no colon is found.
         */
        const _parseErrorMessage = useCallback((message: string): string => {
            const colonIndex = message.indexOf(': ');
            if (colonIndex !== -1) {
                return message.substring(colonIndex + 2).trim();
            }
            return message;
        }, []);

        /**
         * PARSE LOGIC INDEX FROM ERROR MESSAGE PREFIX
         * Server format: "items[X].logics[Y]: <message>" or "items[X].logics[Y].actions[Z]: <message>"
         * Returns the logic index Y, or -1 if not found.
         */
        const parseLogicIndexFromMessage = useCallback((message: string): number => {
            const match = message.match(/\.logics\[(\d+)\]/);
            if (match) {
                return parseInt(match[1], 10);
            }
            return -1;
        }, []);

        /**
         * Gift-related error codes that should be displayed at the action/logic level
         * rather than only at the product level.
         */
        const GIFT_ERROR_CODES = useMemo(
            () => new Set(['GIFT_NOT_SALABLE', 'GIFT_INSUFFICIENT_STOCK', 'GIFT_ADD_FAILED']),
            []
        );

        /**
         * EXTRACT GIFT ITEMS FROM LOGICS
         * Flatten logics → actions, filtering only GIFT type actions.
         * Also maps per-item sync errors (from cartItemReference.error) to the
         * specific action whose SKU matches the error SKU.
         *
         * Error matching strategy (ordered by priority):
         * 1. Match by SKU: error.sku === action.actionProduct.sku
         * 2. Match by logic index: parse "items[X].logics[Y]" from error message prefix
         * 3. Fallback for gift errors: if error is gift-related and no specific match found,
         *    apply to all gift actions (the error affects the entire reference)
         */
        const giftItems = useMemo((): GiftItemForDisplay[] => {
            const items: GiftItemForDisplay[] = [];

            // Type assertion for logics since we have flexible types
            const logics = cartItemReference.logics as CartItemReferenceLogic[] | undefined;
            const referenceError = cartItemReference.error;

            // Pre-compute error matching context when error exists
            let errorLogicIndex = -1;
            let isGiftRelatedError = false;
            let errorMatchedBySku = false;

            if (referenceError) {
                isGiftRelatedError = GIFT_ERROR_CODES.has(referenceError.errorCode);
                errorLogicIndex = parseLogicIndexFromMessage(referenceError.message);

                // Check if the error SKU matches any gift action SKU
                if (referenceError.sku) {
                    errorMatchedBySku =
                        logics?.some(logic =>
                            logic.actions.some(
                                action =>
                                    action.actionType === ActionType.GIFT &&
                                    action.actionProduct?.sku === referenceError.sku
                            )
                        ) ?? false;
                }
            }

            logics?.forEach((logic, logicIndex) => {
                logic.actions
                    .filter(action => action.actionType === ActionType.GIFT && action.actionProduct)
                    .forEach((action, actionIndex) => {
                        const actionSku = action?.actionProduct?.sku ?? '';

                        // Strategy 1: Direct SKU match (error.sku === action product SKU)
                        const hasSkuMatch =
                            referenceError && referenceError.sku && actionSku && referenceError.sku === actionSku;

                        // Strategy 2: Logic index match from error message prefix
                        // Only used when SKU didn't match any action (e.g., error.sku is the main product)
                        const hasLogicIndexMatch =
                            referenceError &&
                            !errorMatchedBySku &&
                            isGiftRelatedError &&
                            errorLogicIndex >= 0 &&
                            logicIndex === errorLogicIndex;

                        // Strategy 3: Fallback for gift errors with no specific match
                        // When error is gift-related but neither SKU nor logic index matched,
                        // apply the error to all gift actions in this reference
                        const hasFallbackMatch =
                            referenceError && isGiftRelatedError && !errorMatchedBySku && errorLogicIndex < 0;

                        const hasErrorForAction = hasSkuMatch || hasLogicIndexMatch || hasFallbackMatch;

                        items.push({
                            key: `gift-${logic.id ?? logic.logicId ?? logicIndex}-${action.id ?? actionIndex}`,
                            logic,
                            action,
                            unavailable: hasErrorForAction || !stockAvailabilityMap[actionSku]?.isSalable,
                            unavailableDescription: undefined,
                        });
                    });
            });

            return items;
        }, [
            cartItemReference.logics,
            cartItemReference.error,
            stockAvailabilityMap,
            parseLogicIndexFromMessage,
            GIFT_ERROR_CODES,
        ]);

        const dataUri = useMemo(
            () => ({
                src: 'https://static.kidsplaza.vn/assets/icons/active-gift.png',
                alt: 'Logo quà tặng',
            }),
            []
        );
        const renderLabelIcon = useCallback(() => {
            const icon = <Icon type="data-uri" className={styles.rightIcon} dataUriProps={dataUri} size="xxsmall" />;
            return (
                <TextIcon
                    containerClassName={styles.textIcon}
                    textClassName={styles.text}
                    leftIcon={icon}
                    text="Free"
                    gap={2}
                    fontSize="xsmall"
                />
            );
        }, [dataUri]);

        /**
         * DATA TRANSFORMATION LAYER
         * Transform GiftItemForDisplay → presentation props (PromotionalItemCardProps)
         */
        const transformToPresentationProps = useCallback(
            (item: GiftItemForDisplay) => {
                const { logic, action, unavailable, unavailableDescription } = item;
                const product = action.actionProduct;

                // Determine badge text based on error/availability state
                let resolvedBadgeText: string | undefined;
                if (unavailableDescription) {
                    resolvedBadgeText = 'Không khả dụng';
                } else if (unavailable) {
                    resolvedBadgeText = 'Hết quà';
                }

                return {
                    innerContainerClassName: innerContainerClassName,
                    containerClassName: cn(styles.container, unavailableDescription && styles.containerError),
                    // Visual display props - derived from action product data
                    title: product?.name ?? '',
                    titleClassName: styles.title,
                    imageSource: product?.small_image?.url || product?.image?.url || '',
                    quantity: action.actionQty ?? 1,
                    priceText: 'Miễn phí', // Gift items are free

                    // Label props - business logic determines what to show
                    labelText: logic.logicName ?? defaultLogicLabel ?? 'Quà tặng khuyến mãi',
                    labelClassName: styles.label,
                    labelIcon: renderLabelIcon(),

                    // Warning/error states - business rules
                    badgeText: resolvedBadgeText,
                    descriptionText: unavailableDescription,

                    // Behavior props
                    mode,
                    size,
                    showActionButton: showActionButton,
                    actionButtonText: mode === 'compact' ? 'Đổi quà' : 'Xem chi tiết',

                    // Business callbacks - consumer handles business logic
                    onActionPress: onActionPress
                        ? () =>
                              onActionPress({
                                  cartItemReference,
                                  logic,
                              })
                        : undefined,
                };
            },
            [
                innerContainerClassName,
                defaultLogicLabel,
                showActionButton,
                renderLabelIcon,
                mode,
                size,
                onActionPress,
                cartItemReference,
            ]
        );

        if (giftItems.length === 0) {
            return null;
        }

        return (
            <>
                {giftItems.map(item => {
                    const presentationProps = transformToPresentationProps(item);
                    return <PromotionalItemCard key={item.key} {...presentationProps} />;
                })}
            </>
        );
    }
);

export default CartGiftList;
